'use client'

import { getTranslate } from 'legacy-src/utilities/i18n'
import React from 'react'

import useFlags from '../../hooks/useFlags'
import ArticlesList from '../counselling/ArticlesList'
import VideoLessonsList from '../onlineLessons/VideoLessonsList'
import TestsList from '../tests/TestsList'
import ContentSection from './ContentSection'
import { PREMIUM_CONTENT } from './hooks/constants'
import MenuCard from './MenuCard'
import {
  NUMBER_OF_ARTICLES,
  NUMBER_OF_BLOG_ARTICLES,
  NUMBER_OF_LESSONS,
  NUMBER_OF_TESTS,
  VIDEO_LESSON_TYPE,
} from './premiumContent.constants'

type Props = {
  readonly articles?: { topic: any; blog: any; aboutTherapy: any }
  readonly tests?: any
  readonly videoLessons?: any
  readonly isPublic?: boolean
  readonly visibleTeasers: string[]
  readonly allowedPremiumContent: string[]
  readonly topicsUrl: string
  readonly testsUrl: string
  readonly blogUrl: string
  readonly aboutTherapyUrl: string
  readonly braineeUrl: string
  readonly poshurshemoUrl: string
  readonly atairuUrl: string
}

const CategoryList: React.FC<Props> = ({
  articles,
  tests,
  videoLessons,
  isPublic = false,
  visibleTeasers,
  allowedPremiumContent,
  topicsUrl,
  testsUrl,
  blogUrl,
  aboutTherapyUrl,
  braineeUrl,
  poshurshemoUrl,
  atairuUrl,
}) => {
  const { allowBrainee, allowPoshurshemo, allowAtairu } = useFlags()
  const { t } = getTranslate()

  const showBrainee =
    allowBrainee &&
    (visibleTeasers?.includes(PREMIUM_CONTENT.BRAINEE) || allowedPremiumContent?.includes(PREMIUM_CONTENT.BRAINEE))
  const showPoshurshemo =
    allowPoshurshemo &&
    (visibleTeasers?.includes(PREMIUM_CONTENT.POSHURSHEMO) ||
      allowedPremiumContent?.includes(PREMIUM_CONTENT.POSHURSHEMO))
  const showAtairu =
    allowAtairu &&
    (visibleTeasers?.includes(PREMIUM_CONTENT.ATAIRU) || allowedPremiumContent?.includes(PREMIUM_CONTENT.ATAIRU))

  const topics = articles.topic?.slice(0, NUMBER_OF_ARTICLES(isPublic))
  const blog = articles.blog?.slice(0, NUMBER_OF_BLOG_ARTICLES)
  const aboutTherapy = articles.aboutTherapy?.slice(0, NUMBER_OF_ARTICLES(isPublic))

  const displayTopics = !!topics?.length
  const displayTests = !!tests?.length
  const displayBlog = !!blog?.length
  const displayAboutTherapy = !!aboutTherapy?.length

  const braineeLessons = videoLessons?.filter(
    (lesson) => lesson.attributes.type === null || lesson.attributes.type === VIDEO_LESSON_TYPE.BRAINEE,
  )
  const poshurshemoLessons = videoLessons?.filter((lesson) => lesson.attributes.type === VIDEO_LESSON_TYPE.POSHURSHEMO)

  const atairuLessons = videoLessons?.filter((lesson) => lesson.attributes.type === VIDEO_LESSON_TYPE.ATAIRU)

  const sectionCount = [
    displayTopics,
    displayTests,
    displayBlog,
    displayAboutTherapy,
    showBrainee,
    showPoshurshemo,
    showAtairu,
  ].filter((x) => x).length

  const bigTabletCols =
    sectionCount === 5
      ? 'big-tablet:grid-cols-5'
      : sectionCount === 4
        ? 'big-tablet:grid-cols-4'
        : 'big-tablet:grid-cols-3'

  return (
    <>
      <div className="container">
        <div className={`mb-6 grid grid-cols-2 flex-col gap-6 tablet:grid-cols-3 ${bigTabletCols}`}>
          {displayTopics && <MenuCard link={topicsUrl} topic="topics" iconName="terapeutic-topics" />}
          {displayTests && <MenuCard link={testsUrl} topic="tests" iconName="tests" />}
          {displayAboutTherapy && <MenuCard link={aboutTherapyUrl} topic="aboutTherapy" iconName="about-therapy" />}
          {displayBlog && <MenuCard link={blogUrl} topic="articles" iconName="blog" />}
          {showBrainee && <MenuCard link={braineeUrl} topic="brainee" iconName="play-bg" />}
          {showPoshurshemo && <MenuCard link={poshurshemoUrl} topic="poshurshemo" iconName="play-bg" />}
          {showAtairu && <MenuCard link={atairuUrl} topic="atairu" iconName="play-bg" />}
        </div>

        {displayTopics && (
          <ContentSection
            title={t('component.premiumContent.topics.title')}
            subtitle={t('component.premiumContent.topics.subtitle')}
            iconName="terapeutic-topics"
            link={topicsUrl}
          >
            <ArticlesList data={topics} isPreview={true} baseLink={topicsUrl} isPublic={isPublic} />
          </ContentSection>
        )}
      </div>
      {displayTests && (
        <>
          <div className="bg-body-bg">
            <div className="container">
              <ContentSection
                title={t('component.premiumContent.tests.title')}
                subtitle={t('component.premiumContent.tests.subtitle')}
                iconName="tests"
                link={testsUrl}
              >
                <TestsList data={tests?.slice(0, NUMBER_OF_TESTS)} isPreview={true} baseLink={testsUrl} />
              </ContentSection>
            </div>
          </div>
        </>
      )}
      <div className="container">
        {displayAboutTherapy && (
          <ContentSection
            title={t('component.premiumContent.aboutTherapy.title')}
            subtitle={t('component.premiumContent.aboutTherapy.subtitle')}
            iconName="about-therapy"
            link={aboutTherapyUrl}
          >
            <ArticlesList data={aboutTherapy} isPreview={true} isPublic={isPublic} baseLink={aboutTherapyUrl} />
          </ContentSection>
        )}

        {displayBlog && (
          <ContentSection
            title={t('component.premiumContent.articles.title')}
            subtitle={t('component.premiumContent.articles.subtitle')}
            iconName="blog"
            link={blogUrl}
          >
            <ArticlesList data={blog} isPreview={true} baseLink={blogUrl} isPublic={isPublic} />
          </ContentSection>
        )}

        {showBrainee && (
          <ContentSection
            title={t('component.premiumContent.brainee.title')}
            subtitle={t('component.premiumContent.brainee.subtitle')}
            iconName="play-bg"
            link={braineeUrl}
          >
            <VideoLessonsList
              data={braineeLessons.slice(0, NUMBER_OF_LESSONS)}
              isPreview={true}
              getItemClassName={(_, index) => (index > 0 ? 'hidden tablet:block' : '')}
              isLocked={!allowedPremiumContent?.includes(PREMIUM_CONTENT.BRAINEE)}
              baseLink={braineeUrl}
            />
          </ContentSection>
        )}

        {showPoshurshemo && (
          <ContentSection
            title={t('component.premiumContent.poshurshemo.title')}
            subtitle={t('component.premiumContent.poshurshemo.subtitle')}
            iconName="play-bg"
            link={poshurshemoUrl}
          >
            <VideoLessonsList
              data={poshurshemoLessons.slice(0, NUMBER_OF_LESSONS)}
              isPreview={true}
              getItemClassName={(_, index) => (index > 0 ? 'hidden tablet:block' : '')}
              isLocked={!allowedPremiumContent?.includes(PREMIUM_CONTENT.POSHURSHEMO)}
              baseLink={poshurshemoUrl}
            />
          </ContentSection>
        )}

        {showAtairu && (
          <ContentSection
            title={t('component.premiumContent.atairu.title')}
            subtitle={t('component.premiumContent.brainee.subtitle')}
            iconName="play-bg"
            link={atairuUrl}
          >
            <VideoLessonsList
              data={atairuLessons.slice(0, NUMBER_OF_LESSONS)}
              isPreview={true}
              getItemClassName={(_, index) => (index > 0 ? 'hidden tablet:block' : '')}
              isLocked={!allowedPremiumContent?.includes(PREMIUM_CONTENT.ATAIRU)}
              baseLink={atairuUrl}
            />
          </ContentSection>
        )}
      </div>
    </>
  )
}

export default CategoryList
